import React, { useState } from "react";
import { toast } from "sonner";
import { Award, Download, Loader2, Lock, ExternalLink } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function CertificateCard({ data, reload }) {
  const [name, setName] = useState(data.user.name || "");
  const [busy, setBusy] = useState(false);
  const cert = data.certificate;

  const issue = async () => {
    setBusy(true);
    try {
      await academy.put("/me", { name });
      await academy.post("/certificate");
      toast.success("Certificate issued");
      reload();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const download = async () => {
    setBusy(true);
    try {
      const r = await academy.get("/certificate/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Reporter-Academy-${cert.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#FFCC00]/25 bg-[#FFCC00]/[0.04] p-6" data-testid="academy-certificate-card">
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#FFCC00]/15 text-[#FFCC00]">{data.certificate_ready ? <Award size={22} /> : <Lock size={20} />}</div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-zinc-50">Certificate of Completion</h2>
          <p className="mt-1 text-[13px] text-zinc-400">A completion certificate, not accreditation. It is not a legal or professional qualification.</p>
          {cert ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 font-mono text-[13px] text-zinc-200" data-testid="academy-cert-id">{cert.id}</span>
              <button data-testid="academy-cert-download" onClick={download} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-[#FFCC00] px-4 py-2 text-[13px] font-semibold text-zinc-950 transition-opacity hover:opacity-90 disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF
              </button>
              <a href={`/academy/verify/${cert.id}`} target="_blank" rel="noreferrer" data-testid="academy-cert-verify-link" className="inline-flex items-center gap-1 text-[13px] text-blue-400 hover:underline">Verification page <ExternalLink size={13} /></a>
            </div>
          ) : data.certificate_ready ? (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Input data-testid="academy-cert-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name as it should appear"
                className="h-10 border-zinc-800 bg-zinc-950 text-zinc-100 sm:max-w-xs" />
              <button data-testid="academy-cert-issue" onClick={issue} disabled={busy || name.trim().length < 2}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#FFCC00] px-4 text-[13px] font-semibold text-zinc-950 disabled:opacity-40">
                {busy && <Loader2 size={15} className="animate-spin" />} Issue my certificate
              </button>
            </div>
          ) : (
            <p className="mt-3 text-[13px] text-zinc-500">Unlocks when you pass the capstone (35 of 50 points), the final exam (80%) and the Ops Desk simulation (90%).</p>
          )}
        </div>
      </div>
    </section>
  );
}
